import { effect } from '@preact/signals-react';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import { setAccessToken as setLocatesClientAccessToken } from 'src/lib/locates/modern';
import { pushServiceSocket, sessionBarrierSocket } from 'src/lib/push';
import { clearMasq } from 'src/store/actions/auth';
import { getAccessTokens, getClientCredentials, getCurrentIsSuperuserEnabledFlag } from 'src/store/selectors';
import type { IsomorphicBindings } from 'src/types/bindings';

/**
 * Syncs various data from the store to external services
 */
@injectable()
export class SyncInternalConfigurationsServiceImpl {
    #disposableStack = new DisposableStack();
    #lastEffectiveAccessToken: string | null | undefined;
    #lastRejectedSocketToken: string | null = null;
    #lastRegularAccessToken: string | null | undefined;
    #log: IsomorphicBindings['Logger'];
    #tokens: ReturnType<typeof getAccessTokens> | undefined;

    constructor(
        @inject('Store') private readonly store: IsomorphicBindings['Store'],
        @inject('ProxyClientService') private readonly proxyClientService: IsomorphicBindings['ProxyClientService'],
        @inject('Logger') logger: IsomorphicBindings['Logger'],
        @inject('SessionService')
        @optional()
        private readonly sessionService?: IsomorphicBindings['SessionService'],
    ) {
        this.#log = logger.getSubLogger({ name: 'SyncInternalConfigurationsService' }, { component: 'auth' });
        this.#disposableStack.defer(
            this.store.subscribeSelector(getAccessTokens, { leading: true }).handle((tokens) => {
                this.#tokens = tokens;
                this.syncAccessTokens();
            }),
        );
        this.#disposableStack.defer(
            this.store
                .subscribeSelector(getClientCredentials, { leading: true })
                .handle((creds) => this.syncClientCredentials(creds)),
        );
        this.#disposableStack.defer(
            this.store
                .subscribeSelector(getCurrentIsSuperuserEnabledFlag, { leading: true })
                .handle((flag) => this.syncIsSuperuserEnabledFlag(flag)),
        );

        if (this.sessionService) {
            this.#disposableStack.defer(
                effect(() => {
                    this.syncAccessTokens(this.sessionService?.accessToken.value);
                }),
            );

            const reportSocketAuthFailure = (token: string | null | undefined) => {
                if (!token || token === this.#lastRejectedSocketToken) {
                    return;
                }
                this.#lastRejectedSocketToken = token;
                void this.sessionService?.refresh(token).catch((error) => {
                    this.#log.error('Failed to report socket token rejection', { error });
                });
            };
            const handlePushAuthFailure = () => {
                const masqueradeToken = this.#tokens?.masqAccessToken?.token;
                if (masqueradeToken && masqueradeToken === this.#lastEffectiveAccessToken) {
                    if (masqueradeToken === this.#lastRejectedSocketToken) {
                        return;
                    }
                    this.#lastRejectedSocketToken = masqueradeToken;
                    this.store.dispatch(clearMasq('Push service rejected the masquerade session'));
                    return;
                }

                reportSocketAuthFailure(this.#lastEffectiveAccessToken);
            };
            const handleBarrierAuthFailure = () => reportSocketAuthFailure(this.#lastRegularAccessToken);
            pushServiceSocket.on('auth.failed', handlePushAuthFailure);
            sessionBarrierSocket.on('auth.failed', handleBarrierAuthFailure);
            this.#disposableStack.defer(() => {
                pushServiceSocket.off('auth.failed', handlePushAuthFailure);
                sessionBarrierSocket.off('auth.failed', handleBarrierAuthFailure);
            });
        }
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    private syncClientCredentials(clientCredentials: ReturnType<typeof getClientCredentials>) {
        // Auth all the internal services to the new token
        this.proxyClientService.setAccessToken(clientCredentials);
    }

    private syncAccessTokens(sessionAccessToken = this.sessionService?.accessToken.value) {
        const tokens = this.#tokens;
        if (!tokens) {
            return;
        }

        // Get the effective access token (masq takes precedence over regular)
        const regularAccessToken = this.sessionService
            ? (sessionAccessToken ?? null)
            : (tokens.accessToken?.token ?? null);
        const effectiveAccessToken = tokens.masqAccessToken?.token ?? regularAccessToken;

        if (effectiveAccessToken !== this.#lastEffectiveAccessToken) {
            this.#lastEffectiveAccessToken = effectiveAccessToken;
            if (effectiveAccessToken === null) {
                this.#lastRejectedSocketToken = null;
            }
            // Null is an explicit transition: every consumer must discard stale auth.
            pushServiceSocket.setAccessToken(effectiveAccessToken);
            this.proxyClientService.setAccessToken(effectiveAccessToken);
            setLocatesClientAccessToken(effectiveAccessToken);
        }

        // Session barrier socket only gets the regular access token (no masquerading)
        if (regularAccessToken !== this.#lastRegularAccessToken) {
            this.#lastRegularAccessToken = regularAccessToken;
            sessionBarrierSocket.setAccessToken(regularAccessToken);
        }
    }

    private syncIsSuperuserEnabledFlag(flag: ReturnType<typeof getCurrentIsSuperuserEnabledFlag>) {
        this.proxyClientService.setSuperuserFlag(flag);
    }
}
